<template>
  <div ref="observerEl" style="min-height: 1px;">
    <a id="pdpRelatedProducts" />
    <div v-for="(list, listindex) in relatedProductsLists" :key="list.title + listindex">
      <div class="pb-24 flex-1 relative">
        <h3 :id="`relatedProductsHeading-${listindex}`" class="type-headline-xl mb-24 mobOnly:px-12">{{ list.title }}</h3>
        <div class="transition-opacity duration-300 opacity-100 desk:layout-container mobOnly:mt-24">
          <client-only>
            <swiper-container
              class="mobOnly:px-24"
              role="region"
              :aria-labelledby="`relatedProductsHeading-${listindex}`"
              :a11y-prev-slide-message="$t('wcag.arialabel.previousSlide')"
              :a11y-next-slide-message="$t('wcag.arialabel.nextSlide')"
              :space-between="uiStore.isMobile ? 2 : 8"
              :slides-per-view="uiStore.isMobile ? 2.5 : 5"
              :slides-offset-before="uiStore.isMobile ? 12 : 0"
              :slides-offset-after="uiStore.isMobile ? 24 : 0"
              :pagination="{
                clickable: true,
                dynamicBullets: true,
              }"
              :navigation="list.products.length > 5"
            >
              <swiper-slide
                v-for="(product, slideIndex) in list.products"
                :key="product.partNo"
              >
                <ProductCard
                  :key="product.partNo + slideIndex"
                  v-track="{product, index: slideIndex, pageName: globalContent.currentStory?.name, pageId: globalContent.currentStory?.id + 1}"
                  :product="product"
                  :enable-quick-buy="true"
                  :point-shop="false"
                  :placement="100"
                  design="small"
                  :show-stock-status="true"
                />
              </swiper-slide>
            </swiper-container>
          </client-only>
        </div>
      </div>
    </div>
    <LoadingIcon v-if="hasStartedLoading && loading" />
  </div>
</template>

<script lang="ts" setup>
import { useUiStore } from '~/store/ui';
import { useGlobalContentStore } from '~/store/globalContent';
import ProductCard from '~/components/product/ProductCard.vue';
import { ProductModel } from '#models/product';
import { useProductImpressionTracking as vTrack } from '~/composables/useTracking';
import { NorceRelatedProductTypeCodes } from '#constants/norceCodes';
import { useProductStore } from '~/store/product';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';

const globalContent = useGlobalContentStore();
const uiStore = useUiStore();
const productStore = useProductStore();

const { $t } = useNuxtApp();

const props = defineProps<{
  productItem: ProductModel,
}>();

type RelatedProductLists = {
  title: string,
  products: ProductModel[]
}

const relatedProductsLists = ref<RelatedProductLists[]>([]);
const loading = ref(false);
const hasStartedLoading = ref(false);
const observerEl = ref<HTMLElement | null>(null);

// Can add more categories if we want
const loadRelations = [NorceRelatedProductTypeCodes.Related];

const loadRelatedProducts = async() => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  const lists: RelatedProductLists[] = [];

  for (const relation of loadRelations) {
    const partNos = props.productItem.relatedProductsByType(relation);
    if (partNos.length > 0) {
      const allProducts = await productStore.getRelatedProducts(partNos);
      // Filter out of stock products - only apply this filter if category != resilient
      const filteredProducts = allProducts.filter(product => {
        const isResilient = product.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
        if (isResilient) {
          return true;
        }
        return product.variants.some(variant => variant.isInStock);
      });

      if (filteredProducts.length > 0) {
        lists.push({
          title: props.productItem.extendedLayout ? $t('productPage.relatedProducts.addOns') : $t('productPage.relatedProducts.' + relation.replace('-', '')),
          products: filteredProducts,
        });
      }
    }
  }

  if (props.productItem.extendedLayout) {
    const allBrandProducts = await productStore.getProductBrandRelatedProducts(props.productItem);
    // Filter out of stock products - only apply this filter if category != resilient
    const filteredBrandProducts = allBrandProducts.filter(product => {
      const isResilient = product.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
      if (isResilient) {
        return true;
      }
      return product.variants.some(variant => variant.isInStock);
    });

    if (filteredBrandProducts.length > 0) {
      lists.push({
        title: $t('productPage.relatedProducts.fromBrand', { brand : props.productItem.brand.name }),
        products: filteredBrandProducts,
      });
    }
  }

  relatedProductsLists.value = lists;
  loading.value = false;
};

const startLoading = () => {
  if (hasStartedLoading.value) {
    return;
  }
  hasStartedLoading.value = true;
  loadRelatedProducts();
};

onMounted(() => {
  if (!observerEl.value) {
    return;
  }
  let observer: IntersectionObserver | null = null;
  let waitTimeout: ReturnType<typeof setTimeout> | null = null;
  const setupObserver = () => {
    if (!observerEl.value) {
      return;
    }
    const observerOptions = {
      root: null,
      threshold: 0.1,
      rootMargin: '-50px 0px',
    };
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
          startLoading();
          observer?.unobserve(entry.target);
        }
      });
    }, observerOptions);
    observer.observe(observerEl.value);
  };

  const WAIT_STEP_MS = 100;
  const MAX_WAIT_MS = 800;
  let waitedMs = 0;
  const scheduleObserver = () => {
    if (window.scrollY > 0 && waitedMs < MAX_WAIT_MS) {
      waitedMs += WAIT_STEP_MS;
      waitTimeout = setTimeout(scheduleObserver, WAIT_STEP_MS);
      return;
    }
    setupObserver();
  };

  scheduleObserver();
  onBeforeUnmount(() => {
    if (waitTimeout) {
      clearTimeout(waitTimeout);
    }
    if (observer && observerEl.value) {
      observer.unobserve(observerEl.value);
    }
  });
});

const jsonLd = computed(() => {
  let numberOfItems = 0;
  return {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    // numberOfItems: relatedProductsLists.value.length,
    itemListElement: relatedProductsLists.value.map(list => {
      return list.products.map(productItem => {
        numberOfItems++;
        const activeVariant = productItem?.variants[0];
        return {
          '@type': 'Product',
          url: productItem?.url,
          name: productItem?.name,
          image: productItem?.imageList,
          description: productItem?.metaDescription,
          sku: productItem?.partNo,
          mpn: productItem?.partNo,
          brand: {
            '@type': 'Brand',
            name: productItem?.brand?.name,
          },
          offers: {
            '@type': 'Offer',
            url: productItem?.url,
            priceCurrency: globalContent?.getCurrency,
            itemCondition: 'https://schema.org/NewCondition',
            availability: activeVariant?.canAddToCart ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          },
        };
      });
    }),
    numberOfItems,
  };
});

/*
useJsonld(jsonLd);*/
</script>